import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton,
    Button, Typography, Box, Alert, CircularProgress,
    TextField, InputAdornment
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    People as PeopleIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import api from '../services/api';

const ITEMS_PER_PAGE = 50; // Carrega 50 registros por vez

function Clientes() {
    const navigate = useNavigate();
    const [clientes, setClientes] = useState([]);
    const [clientesComProblema, setClientesComProblema] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [offset, setOffset] = useState(0);
    const observerTarget = useRef(null);

    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState(''); // Input do usuário (não filtrado)

    // Debounce para pesquisa - só pesquisa após 3 caracteres e 500ms de inatividade
    useEffect(() => {
        const timer = setTimeout(() => {
            // Só pesquisa se tiver 3 ou mais caracteres, ou se estiver vazio (limpar busca)
            if (searchInput.length === 0 || searchInput.length >= 3) {
                setSearch(searchInput);
            }
        }, 500); // Aguarda 500ms após parar de digitar

        return () => clearTimeout(timer);
    }, [searchInput]);

    // Carrega clientes iniciais
    useEffect(() => {
        resetAndLoad();
    }, [search]);

    // Configura o Intersection Observer para detectar quando chegar no final
    // Desabilita infinite scroll quando há busca ativa
    useEffect(() => {
        // Se houver busca, não usa infinite scroll
        if (search) {
            return;
        }
        
        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting && hasMore && !loadingMore && !loading && !search) {
                    loadMoreClientes();
                }
            },
            { threshold: 0.1 }
        );

        const currentTarget = observerTarget.current;
        if (currentTarget) {
            observer.observe(currentTarget);
        }

        return () => {
            if (currentTarget) {
                observer.unobserve(currentTarget);
            }
        };
    }, [hasMore, loadingMore, loading, search]);

    // Carrega o contador total de clientes com problemas (independente da paginação)
    useEffect(() => {
        loadContadorProblemas();
        
        // Atualiza o contador quando a página receber foco (ex: voltar de editar cliente)
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                loadContadorProblemas();
            }
        };
        
        const handleFocus = () => {
            loadContadorProblemas();
        };
        
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);
        
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, []);

    const loadContadorProblemas = async () => {
        try {
            const response = await api.get('/clientes/contar-problemas');
            setClientesComProblema(response.data.total || 0);
        } catch (error) {
            console.error('Erro ao carregar contador de problemas:', error);
        }
    };

    const resetAndLoad = async () => {
        setClientes([]);
        setOffset(0);
        setHasMore(true);
        await loadClientes(0, true);
    };

    const loadClientes = async (currentOffset = 0, isInitial = false) => {
        if (isInitial) {
            setLoading(true);
        } else {
            setLoadingMore(true);
        }

        try {
            // Se houver busca, não usa paginação (busca retorna todos os resultados)
            // Se não houver busca, usa paginação normal
            let url = '/clientes';
            if (search) {
                url += `?search=${encodeURIComponent(search)}`;
            } else {
                url += `?limit=${ITEMS_PER_PAGE}&offset=${currentOffset}`;
            }
            
            const response = await api.get(url);
            const newClientes = response.data || [];
            
            if (isInitial) {
                setClientes(newClientes);
            } else {
                // Se houver busca, não adiciona (já tem todos os resultados)
                // Se não houver busca, adiciona mais registros
                if (!search) {
                    setClientes(prev => [...prev, ...newClientes]);
                }
            }

            // Se houver busca, não há mais registros (já retornou todos)
            // Se não houver busca, verifica se retornou menos que ITEMS_PER_PAGE
            if (search) {
                setHasMore(false);
            } else {
                setHasMore(newClientes.length === ITEMS_PER_PAGE);
                setOffset(currentOffset + newClientes.length);
            }
        } catch (error) {
            console.error('Erro ao carregar clientes:', error);
            console.error('Detalhes do erro:', error.response?.data);
            if (isInitial) {
                setClientes([]);
            }
        } finally {
            if (isInitial) {
                setLoading(false);
            } else {
                setLoadingMore(false);
            }
        }
    };

    const loadMoreClientes = () => {
        if (!loadingMore && hasMore) {
            loadClientes(offset, false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Tem certeza que deseja excluir este cliente?')) {
            try {
                await api.delete(`/clientes/${id}`);
                // Remove o cliente da lista local
                setClientes(prev => prev.filter(c => c.id !== id));
                // Atualiza o contador de problemas
                loadContadorProblemas();
            } catch (error) {
                console.error('Erro ao deletar cliente:', error);
            }
        }
    };

    return (
        <Box>
            {/* Header destacado para indicar a tela ativa */}
            <Box 
                sx={{ 
                    mb: 3, 
                    p: 2, 
                    backgroundColor: 'primary.main', 
                    color: 'primary.contrastText',
                    borderRadius: 2,
                    boxShadow: 2
                }}
            >
                <Typography 
                    variant="h4" 
                    component="h1" 
                    sx={{ 
                        fontSize: { xs: '1.5rem', sm: '2rem' },
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <PeopleIcon sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
                    Clientes
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                    Gerencie o cadastro de clientes
                </Typography>
            </Box>

            <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2 }}>
                <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
                    {clientesComProblema > 0 && (
                        <Alert severity="warning" sx={{ mt: 1, maxWidth: { xs: '100%', sm: 600 } }}>
                            {clientesComProblema} cliente(s) com data de nascimento inválida (destacados em amarelo)
                        </Alert>
                    )}
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/clientes/novo')}
                    fullWidth={false}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                    Novo Cliente
                </Button>
            </Box>

            <Box sx={{ mb: 3 }}>
                <TextField
                    fullWidth
                    placeholder="Buscar por nome, CPF, email, telefone ou cidade... (mín. 3 caracteres)"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <TableContainer 
                component={Paper} 
                sx={{ 
                    overflowX: 'auto', 
                    maxWidth: '100%',
                    maxHeight: '70vh',
                    overflowY: 'auto',
                    position: 'relative'
                }}
            >
                <Table sx={{ minWidth: 650 }}>
                    <TableHead sx={{ position: 'sticky', top: 0, zIndex: 10 }}>
                        <TableRow>
                            <TableCell sx={{
                                fontWeight: 600,
                                backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                    ? 'rgba(25, 25, 25, 0.95)' 
                                    : 'rgba(255, 255, 255, 0.95)',
                                backdropFilter: 'blur(8px)',
                                borderBottom: (theme) => `2px solid ${theme.palette.divider}`
                            }}>
                                Nome
                            </TableCell>
                            <TableCell sx={{ 
                                display: { xs: 'none', md: 'table-cell' },
                                fontWeight: 600,
                                backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                    ? 'rgba(25, 25, 25, 0.95)' 
                                    : 'rgba(255, 255, 255, 0.95)',
                                backdropFilter: 'blur(8px)',
                                borderBottom: (theme) => `2px solid ${theme.palette.divider}`
                            }}>
                                CPF
                            </TableCell>
                            <TableCell sx={{ 
                                display: { xs: 'none', sm: 'table-cell' },
                                fontWeight: 600,
                                backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                    ? 'rgba(25, 25, 25, 0.95)' 
                                    : 'rgba(255, 255, 255, 0.95)',
                                backdropFilter: 'blur(8px)',
                                borderBottom: (theme) => `2px solid ${theme.palette.divider}`
                            }}>
                                Telefone
                            </TableCell>
                            <TableCell sx={{ 
                                display: { xs: 'none', lg: 'table-cell' },
                                fontWeight: 600,
                                backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                    ? 'rgba(25, 25, 25, 0.95)' 
                                    : 'rgba(255, 255, 255, 0.95)',
                                backdropFilter: 'blur(8px)',
                                borderBottom: (theme) => `2px solid ${theme.palette.divider}`
                            }}>
                                Email
                            </TableCell>
                            <TableCell sx={{ 
                                display: { xs: 'none', md: 'table-cell' },
                                fontWeight: 600,
                                backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                    ? 'rgba(25, 25, 25, 0.95)' 
                                    : 'rgba(255, 255, 255, 0.95)',
                                backdropFilter: 'blur(8px)',
                                borderBottom: (theme) => `2px solid ${theme.palette.divider}`
                            }}>
                                Cidade
                            </TableCell>
                            <TableCell sx={{
                                align: 'right',
                                fontWeight: 600,
                                backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                    ? 'rgba(25, 25, 25, 0.95)' 
                                    : 'rgba(255, 255, 255, 0.95)',
                                backdropFilter: 'blur(8px)',
                                borderBottom: (theme) => `2px solid ${theme.palette.divider}`
                            }}>
                                Ações
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    <CircularProgress size={24} sx={{ my: 2 }} />
                                </TableCell>
                            </TableRow>
                        ) : clientes.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">Nenhum cliente encontrado</TableCell>
                            </TableRow>
                        ) : (
                            <>
                                {clientes.map((cliente) => (
                            <TableRow 
                                key={cliente.id}
                                sx={{
                                    backgroundColor: cliente.tem_problema_validacao ? 'rgba(255, 193, 7, 0.2)' : 'inherit',
                                    '&:hover': {
                                        backgroundColor: cliente.tem_problema_validacao ? 'rgba(255, 193, 7, 0.4)' : 'action.hover'
                                    }
                                }}
                            >
                                <TableCell>
                                    <Box>
                                        <Typography variant="body1" component="span" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                            {cliente.nome}
                                        </Typography>
                                        {cliente.tem_problema_validacao && (
                                            <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                                                ⚠️ Data inválida
                                            </Typography>
                                        )}
                                        <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', md: 'none' }, mt: 0.5 }}>
                                            CPF: {cliente.cpf} | {cliente.telefone || 'Sem telefone'}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{cliente.cpf}</TableCell>
                                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{cliente.telefone || '-'}</TableCell>
                                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{cliente.email || '-'}</TableCell>
                                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{cliente.cidade || '-'}</TableCell>
                                <TableCell align="right">
                                    <IconButton 
                                        onClick={() => navigate(`/clientes/editar/${cliente.id}`)}
                                        color="primary"
                                        size="small"
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton 
                                        onClick={() => handleDelete(cliente.id)}
                                        color="error"
                                        size="small"
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                                ))}
                                {/* Elemento sentinela para detectar quando chegar no final */}
                                {hasMore && (
                                    <TableRow ref={observerTarget}>
                                        <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                            {loadingMore && <CircularProgress size={24} />}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

export default Clientes; 